import React from 'react';
import Markdown from '@/components/Markdown';
import { saveTemporaryUserInfo } from './redux/actions/hairDiagnosis';

export const fixedFetch = (path, config) =>
  new Promise((resolve, reject) => {
    fetch(path, config)
      .then(response => response.json())
      .then(json => resolve(json))
      .catch(err => reject(err));
  });

export const isEmptyObj = obj => Object.keys(obj).length === 0 && obj.constructor === Object;

export const getScreenWidth = () =>
  window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

export const getScreenSize = () => {
  const width = getScreenWidth();
  return [600, 900, 1200, 1800, Infinity].findIndex(item => item > width);
};

export const throttle = (callback, wait, context = this) => {
  let timeout = null;
  let callbackArgs = null;

  const later = () => {
    callback.apply(context, callbackArgs);
    timeout = null;
  };

  return () => {
    if (!timeout) {
      callbackArgs = arguments;
      timeout = setTimeout(later, wait);
    }
  };
};

export const toParameterString = params => {
  Object.values(params).forEach(item => {
    if (typeof item !== 'number' && (typeof item !== 'string' || item.length <= 0)) {
      console.error('Invalid Params: ', params);
      throw new Error('Arguments must be number or non-empty strings!');
    }
  });
  const list = Object.keys(params).map(key => `${key}=${params[key]}`);
  return list.join('&');
};

export const getCurrency = locale => {
  switch (locale) {
    case 'ja_jp':
      return 'JPY';
    case 'zh_cn':
      return 'CNY';
    case 'ko_kr':
      return 'KRW';
    case 'en':
      return 'USD';
    case 'en_id':
      return 'IDR';
    case 'en_hk':
      return 'CNY';
    case 'th_th':
      return 'THB';
    case 'zh_hk':
      return 'CNY';
    case 'zh_tw':
      return 'CNY';
    default:
      return 'USD';
  }
};

export const makeCanonicalTagObj = (locale, path) => ({
  rel: 'canonical',
  href: `${window.location.protocol}//${window.location.host}/${locale}${path}`,
});

export const makeHreflangTagArr = (regions, path) => {
  const template = (locale, hreflang) => ({
    rel: 'alternate',
    hreflang,
    href: `${window.location.protocol}//${window.location.host}/${locale}${path}`,
  });

  let _regions = regions;
  if (_regions[0] === 'All') {
    _regions = ['Global', 'Hong Kong', 'Indonesia', 'Japan', 'Korea', 'Malaysia', 'Singapore', 'Taiwan', 'Thailand'];
  }

  return _regions.reduce((acc, region) => {
    switch (region) {
      case 'Global':
        return [template('en', 'en'), ...acc];
      case 'Hong Kong':
        return [template('en_hk', 'en-CN'), template('zh_hk', 'zh-CN'), ...acc];
      case 'Indonesia':
        return [template('en_id', 'en-ID'), ...acc];
      case 'Japan':
        return [template('ja_jp', 'ja'), ...acc];
      case 'Korea':
        return [template('ko_kr', 'ko-KR'), ...acc];
      case 'Malaysia':
        return [template('en_my', 'en-MY'), ...acc];
      case 'Singapore':
        return [template('en_sg', 'en-SG'), ...acc];
      case 'Taiwan':
        return [template('zh_tw', 'zh-TW'), ...acc];
      case 'Thailand':
        return [template('th_th', 'th'), ...acc];
      default:
        return acc;
    }
  }, {});
};

export const errorLogger = error => {
  let e = error;
  if (e.data) e = e.data;
  if (e.message) e = e.message;
  if (process.env.NODE_ENV !== 'production') {
    console.error(`Unhandled rejection caught with reason: ${JSON.stringify(e)}.`);
  }
};

const keys = { 37: 1, 38: 1, 39: 1, 40: 1 };

const preventDefault = event => {
  const e = event || window.event;
  if (e.preventDefault) e.preventDefault();
  e.returnValue = false;
};

const preventDefaultForScrollKeys = e => {
  if (keys[e.keyCode]) {
    preventDefault(e);
    return false;
  }
  return undefined;
};

export const disableScroll = () => {
  window.addEventListener('DOMMouseScroll', preventDefault, false);
  window.onwheel = preventDefault;
  window.onmousewheel = preventDefault;
  document.onmousewheel = preventDefault;
  window.ontouchmove = preventDefault;
  document.onkeydown = preventDefaultForScrollKeys;
  window.ontouchmove = preventDefault;
};

export const enableScroll = () => {
  window.removeEventListener('DOMMouseScroll', preventDefault, false);
  window.onmousewheel = null;
  document.onmousewheel = null;
  window.onwheel = null;
  window.ontouchmove = null;
  document.onkeydown = null;
  window.ontouchmove = null;
};

export function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0; // eslint-disable-line
    const v = c == 'x' ? r : (r & 0x3) | 0x8; // eslint-disable-line
    return v.toString(16);
  });
}

export const scrollToElementById = id => {
  const element = document.getElementById(id);
  if (element) window.scrollBy(0, element.getBoundingClientRect().top);
};

export const shouldLoadData = (model, data) => {
  if (typeof model !== typeof data) return true;

  if (Array.isArray(model) && model.map((subModel, i) => shouldLoadData(subModel, data[i])).some(bool => bool)) {
    return true;
  } else if (
    typeof model === 'object' &&
    (data.isLink ||
      Object.keys(model)
        .map(key => shouldLoadData(model[key], data[key]))
        .some(bool => bool))
  ) {
    return true;
  }
  return false;
};

/* eslint-disable */
export const dataUrlToBinary = (t, e) => {
  let i, n, r, o, s, a, c, h, l, u;
  for (
    i = ';base64,',
      c = t.split(i),
      n = c[1],
      e > 0 && ((u = 4 * Math.ceil(4 * e / 3 / 4)), (n = n.substr(0, u))),
      h = window.atob(n),
      a = h.length,
      r = new Uint8Array(a),
      o = s = 0,
      l = a;
    l >= 0 ? l >= s : s >= l;
    o = l >= 0 ? ++s : --s
  )
    r[o] = h.charCodeAt(o);
  return r;
};
/* eslint-enable */

export const getImageSize = (file, cb) => {
  const URL = window.URL || window.webkitURL;
  const img = new Image();
  img.onload = function imageLoad() {
    if (cb) {
      cb(this.width, this.height);
    }
  };
  img.src = URL.createObjectURL(file);
};

export const cookieFactory = () => {
  const setCookie = (name, value, time = 24 * 60 * 60 * 1000) => {
    const exp = new Date();
    exp.setTime(exp.getTime() + time);
    console.log(exp);
    document.cookie = name + "=" + escape (value) + ";expires=" + exp.toGMTString();
  };
  const getCookie = name => {
    const arr = document.cookie.match(new RegExp("(^| )" + name + "=([^;]*)(;|$)"));
    if (arr != null) return unescape(arr[2]);
    return null;
  };
  const delCookie = name => {
    const exp = new Date();
    exp.setTime(exp.getTime() - 1);
    const cval = getCookie(name);
    if(cval!=null) document.cookie= name + "="+cval+";expires="+exp.toGMTString();
  };
  return {
    setCookie,
    getCookie,
    delCookie,
  };
};
export const checkOrientation = vm => {
  let orientationStr = '';
  function init() {
    const updateOrientation = () => {
      let { orientation } = window.orientation || 0;
      switch (orientation) {
        case 90:
        case -90:
          orientation = 'landscape';
          orientationStr = 'landscape';
          vm.setState({ orientationStr });
          break;
        default:
          orientation = 'portrait';
          orientationStr = 'portrait';
          vm.setState({ orientationStr });
          break;
      }
    };
    window.addEventListener('orientationchange', updateOrientation, false);
    updateOrientation();
  }
  window.addEventListener('DOMContentLoaded', init, false);
};

export const lsSetItem = (key, value, restore = false) => {
  localStorage.setItem(key, value);
  if (restore) {
    const key = 'RESTORE_LIST';
    let list = localStorage.getItem(key);
    list = list.reduce((acc, key) => (acc.findIndex(k => k === key) >= 0 ? [...acc, key] : acc), []);
    localStorage.setItem(key, list);
  }
};

export const lsGetItem = key => localStorage.getItem(key);

export const lsRemoveItem = key => localStorage.removeItem(key);

export const salonSolutionFilter = (salonSolutionData, salonSettingData) => {
  let hairCare = [];
  let scalpCare = [];
  let hairAndScalpCare = [];

  const menus = (salonSettingData && salonSettingData.salonMenus) || false;
  const treatmentMenus = (menus && menus.treatments) || false;
  const spaMenus = (menus && menus.spas) || false;
  const hairCares = salonSolutionData.hairCare || false;
  const scalpCares = salonSolutionData.scalpCare || false;
  const hairAndScalpCares = salonSolutionData.hairAndScalpCare || false;

  if (treatmentMenus && hairCares) {
    hairCare = hairCares.reduce((acc, care) => {
      const target = treatmentMenus.find(menu => menu.slug === care.slug);
      if (!target) {
        return acc;
      }
      return [...acc, { ...care, salonSetting: target }];
    }, []);
  }

  if (spaMenus && scalpCares) {
    scalpCare = scalpCares.reduce((acc, care) => {
      const target = spaMenus.find(menu => menu.slug === care.slug);
      if (!target) {
        return acc;
      }
      return [...acc, { ...care, salonSetting: target }];
    }, []);
  }

  if (spaMenus && hairAndScalpCares) {
    hairAndScalpCare = hairAndScalpCares.reduce((acc, care) => {
      const target = spaMenus.find(menu => menu.slug === care.slug);
      if (!target) {
        return acc;
      }
      return [...acc, { ...care, salonSetting: target }];
    }, []);
  }

  const result = hairCare.length + scalpCare.length + hairAndScalpCare.length > 0;

  return {
    ...salonSolutionData,
    hairCare,
    scalpCare,
    hairAndScalpCare,
    basicSetting: salonSettingData.basicSetting,
    result,
  };
};

export const homeCareItemFilter = (homeCareList, salonSetting) => {
  let hairItems;
  let scalpItems;
  let hairAndScalpItems;
  const hairConcerns = [];
  const scalpConcerns = [];
  const hairAndScalpConcerns = [];
  const authSalonSettingData = salonSetting;

  const hairCareItems = (homeCareList && homeCareList.hairCareItems) || false;
  const scalpCareItems = (homeCareList && homeCareList.scalpCareItems) || false;
  const hairAndScalpCareItems = (homeCareList && homeCareList.hairAndScalpCareItems) || false;
  const settingItems = (salonSetting && salonSetting.homeCareItems) || false;

  if (hairCareItems && settingItems) {
    hairItems = hairCareItems.reduce((acc, item) => {
      const flg = item.concernGroup.reduce((acc2, concern) => {
        if (settingItems.reduce((acc3, slug) => (slug === concern.slug ? true : acc3), false)) {
          hairConcerns.push(concern);
          return true;
        }
        return acc2;
      }, false);
      return flg ? [...acc, item] : acc;
    }, []);

    authSalonSettingData.homeCareHairSlugs = hairConcerns.reduce((acc, concern) => [...acc, concern.slug], []);
  }

  if (scalpCareItems && settingItems) {
    scalpItems = scalpCareItems.reduce((acc, item) => {
      const flg = item.concernGroup.reduce((acc2, concern) => {
        if (settingItems.reduce((acc3, slug) => (slug === concern.slug ? true : acc3), false)) {
          scalpConcerns.push(concern);
          return true;
        }
        return acc2;
      }, false);
      return flg ? [...acc, item] : acc;
    }, []);

    authSalonSettingData.homeCareScalpSlugs = scalpConcerns.reduce((acc, concern) => [...acc, concern.slug], []);
  }

  if (hairAndScalpCareItems && settingItems) {
    hairAndScalpItems = hairAndScalpCareItems.reduce((acc, item) => {
      const flg = item.concernGroup.reduce((acc2, concern) => {
        if (settingItems.reduce((acc3, slug) => (slug === concern.slug ? true : acc3), false)) {
          hairAndScalpConcerns.push(concern);
          return true;
        }
        return acc2;
      }, false);
      return flg ? [...acc, item] : acc;
    }, []);

    authSalonSettingData.homeCareHairAndScalpSlugs = hairAndScalpConcerns.reduce(
      (acc, concern) => [...acc, concern.slug],
      []
    );
  }

  return {
    hairItems,
    hairConcerns,
    scalpItems,
    scalpConcerns,
    hairAndScalpItems,
    hairAndScalpConcerns,
    authSalonSettingData,
  };
};

export const getLocaleDate = dateStr => {
  if (typeof dateStr === 'string') {
    const regexp = /-|T|:|\.|Z/;
    const dateAry = dateStr.slice(0, -1).split(regexp);
    const date = new Date(
      Date.UTC(dateAry[0], dateAry[1] - 1, dateAry[2], dateAry[3], dateAry[4], dateAry[5], dateAry[6])
      // Date.UTC(...dateAry)
    );
    return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
  }
  return '';
};

export const formatDate = date => {
  const d = new Date(date);
  let month = `${d.getMonth() + 1}`;
  let day = `${d.getDate()}`;
  const year = d.getFullYear();

  if (month.length < 2) month = `0${month}`;
  if (day.length < 2) day = `0${day}`;

  return [year, month, day].join('/');
};

export const sublimicDiagnosisFormatedData = (sublimicDiagnosis, sublimicDiagnosisResults) => {
  let formatedData = {};
  let stylistDiagnosisResults = { result: {} };
  if (sublimicDiagnosis.stylistDiagnosisResults.result) {
    const { treatments, hairDiagnosis, scalpDiagnosis } = sublimicDiagnosis.stylistDiagnosisResults.result;
    if (Object.keys(treatments).length !== 0 && treatments.constructor === Object) {
      stylistDiagnosisResults = {
        ...stylistDiagnosisResults,
        result: { ...stylistDiagnosisResults.result, treatments },
      };
    }
    let hairDiagnosisFormat = {};
    if (hairDiagnosis.hearing && hairDiagnosis.hearing.Questions && hairDiagnosis.hearing.Questions.length > 0) {
      hairDiagnosisFormat = { ...hairDiagnosisFormat, hearing: hairDiagnosis.hearing };
    }
    if (
      hairDiagnosis.stylistDiagnosis &&
      hairDiagnosis.stylistDiagnosis.Questions &&
      hairDiagnosis.stylistDiagnosis.Questions.length > 0
    ) {
      hairDiagnosisFormat = { ...hairDiagnosisFormat, stylistDiagnosis: hairDiagnosis.stylistDiagnosis };
    }
    if (Object.keys(hairDiagnosisFormat).length !== 0 && hairDiagnosisFormat.constructor === Object) {
      stylistDiagnosisResults = {
        ...stylistDiagnosisResults,
        result: { ...stylistDiagnosisResults.result, hairDiagnosis: hairDiagnosisFormat },
      };
    }
    let scalpDiagnosisFormat = {};
    if (scalpDiagnosis.hearing && scalpDiagnosis.hearing.Questions && scalpDiagnosis.hearing.Questions.length > 0) {
      scalpDiagnosisFormat = { ...scalpDiagnosisFormat, hearing: scalpDiagnosis.hearing };
    }
    if (
      scalpDiagnosis.stylistDiagnosis &&
      scalpDiagnosis.stylistDiagnosis.Questions &&
      scalpDiagnosis.stylistDiagnosis.Questions.length > 0
    ) {
      scalpDiagnosisFormat = { ...scalpDiagnosisFormat, stylistDiagnosis: scalpDiagnosis.stylistDiagnosis };
    }
    if (Object.keys(scalpDiagnosisFormat).length !== 0 && scalpDiagnosisFormat.constructor === Object) {
      stylistDiagnosisResults = {
        ...stylistDiagnosisResults,
        result: { ...stylistDiagnosisResults.result, scalpDiagnosis: scalpDiagnosisFormat },
      };
    }
  }
  if (
    Object.keys(stylistDiagnosisResults.result).length !== 0 &&
    stylistDiagnosisResults.result.constructor === Object
  ) {
    formatedData = { ...formatedData, stylistDiagnosisResults };
  }

  if (Object.keys(sublimicDiagnosisResults).length > 0) {
    // if (Object.keys(result).length > 0) {
    const getSlug = data => (data && data.slug ? data.slug : '');
    const {
      diagnosisResults,
      selectedCareMenu,
      selectedHairCare,
      selectedScalpCare,
      recommendedHairCare,
      recommendedScalpCare,
      selectedHomeCareHairItems,
      selectedHomeCareScalpItems,
      recommendedHomeCareHairItems,
      recommendedHomeCareScalpItems,
      selectedInFillItem,
      selectedMaskItem,
      selectedAdenovitalItem,
      selectedAromaItem,
      selectedPreCleanseShampooItem,
      recommendedInFillItem,
      recommendedMaskItem,
      recommendedAdenovitalItem,
      recommendedAromaItem,
      recommendedPreCleanseShampooItems,
      // } = result;
    } = sublimicDiagnosisResults;
    diagnosisResults.hairCurrentStatus = getSlug(diagnosisResults.hairCurrentStatus);
    diagnosisResults.hairNecessaryCare = getSlug(diagnosisResults.hairNecessaryCare);
    diagnosisResults.scalpCurrentStatus = getSlug(diagnosisResults.scalpCurrentStatus);
    diagnosisResults.scalpNecessaryCare = getSlug(diagnosisResults.scalpNecessaryCare);
    const customizedSalonSolutions = { hair: getSlug(selectedHairCare), scalp: getSlug(selectedScalpCare) };
    const recommendedSalonSolutions = { hair: getSlug(recommendedHairCare), scalp: getSlug(recommendedScalpCare) };
    const customizedHomeCareItems = {
      hair: selectedHomeCareHairItems.map(item => getSlug(item)),
      scalp: selectedHomeCareScalpItems.map(item => getSlug(item)),
    };
    const recommendedHomeCareItems = {
      hair: recommendedHomeCareHairItems.map(item => getSlug(item)),
      scalp: recommendedHomeCareScalpItems.map(item => getSlug(item)),
    };

    formatedData = {
      ...formatedData,
      sublimicDiagnosisResult: {
        result: {
          selectedCareMenu,
          diagnosisResults,
          recommendedSalonSolutions,
          customizedSalonSolutions,
          recommendedHomeCareItems,
          customizedHomeCareItems,
          selectedInFillItem: getSlug(selectedInFillItem),
          selectedMaskItem: getSlug(selectedMaskItem),
          selectedAdenovitalItem: getSlug(selectedAdenovitalItem),
          selectedAromaItem: getSlug(selectedAromaItem),
          selectedPreCleanseShampooItem: getSlug(selectedPreCleanseShampooItem),
          recommendedInFillItem: getSlug(recommendedInFillItem),
          recommendedMaskItem: getSlug(recommendedMaskItem),
          recommendedAdenovitalItem: getSlug(recommendedAdenovitalItem),
          recommendedAromaItem: getSlug(recommendedAromaItem),
          recommendedPreCleanseShampooItems: getSlug(recommendedPreCleanseShampooItems),
        },
      },
    };
  }

  if (
    sublimicDiagnosis.userSurveyResult &&
    sublimicDiagnosis.userSurveyResult.result &&
    sublimicDiagnosis.userSurveyResult.result.length > 0
  ) {
    formatedData = { ...formatedData, userSurveyResult: sublimicDiagnosis.userSurveyResult };
  }

  // stylistDiagnosisResults の添字変更の暫定対処
  if (formatedData.stylistDiagnosisResults) {
    formatedData.stylistDiagnosisResult = formatedData.stylistDiagnosisResults;
    delete formatedData.stylistDiagnosisResults;
  }
  return formatedData;
};

/**
 * UA判定を行う
 */
export const determineUserAgent = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const iOS = {
    isIOS: userAgent.search(/iphone|ipad|ipod/) > -1,
    isIPhone: userAgent.includes('iphone'),
    isIPad: userAgent.includes('ipad'),
    isIPod: userAgent.includes('ipod'),
  };

  // TODO: Android のタブレット判定必要？
  const android = {
    isAndroid: userAgent.search(/android/) > -1,
  };

  return { ...iOS, ...android };
};

/**
 * イテレート表示用のキーを自動生成する
 */
export const generateKey = (purpose, index) => `${purpose}-${index}`;

/**
 * 日本語の読点「、」がある場合は改行させる
 */
export const createLineBreak = allText => {
  if (!allText) return false;

  const lines = allText.split('、');
  return lines.map(
    (line, index) =>
      index === lines.length - 1 ? (
        <div key={generateKey('line-break', index)}>{line}</div>
      ) : (
        <div key={generateKey('line-break', index)}>{`${line}、`}</div>
      )
  );
};

/**
 * 黄金比でサイズを返す
 */
export const calculateGoldenRatio = ({ orientation, size, own }) => {
  let resultSize = 0;
  const coefficientMultiplication = 1.618;
  const coefficientSubtraction = 0.62;

  switch (orientation) {
    case 'PORTRAIT':
      if (own === 'VERTICAL') resultSize = size * coefficientSubtraction;
      if (own === 'HORIZONTAL') resultSize = size * coefficientMultiplication;
      break;
    case 'LANDSCAPE':
      if (own === 'VERTICAL') resultSize = size * coefficientMultiplication;
      if (own === 'HORIZONTAL') resultSize = size * coefficientSubtraction;
      break;
    default:
      resultSize = 0;
      break;
  }

  return resultSize;
};

/**
 * マークダウンを HTML に変換する際に改行させる
 */
export const insertNewLine = ({ key, description, component }) => {
  if (!(key && description && component)) return;

  return description
    .split(/<br>|<BR>/g)
    .map((text, index) => <Markdown key={generateKey(key, index)} src={text} Component={component} />);
};



// WEBPACK FOOTER //
// ./src/utils.js